import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal'
import { promocodeRegex } from './promocode-regex'

export const promotionUnitOptions = [
  'fixedAmountOffTotal',
  // 'fixedAmountOffEach',
  'percentOffEach',
  'percentOffSingle'
] as const

export const PromotionUnits = enumOptionsToObject(promotionUnitOptions)
export type PromotionUnit = keyof typeof PromotionUnits

export const promotionConditionFieldOptions = [
  'outfitterIds',
  'outfitterGroupIds',
  // 'productId',
  'rideBeginDate',
  'bookingDate',
  'outfitterProgram',
  // 'modelFamilyId',
  'numberOfRedemptions',
  'productLines',
  'productIds'
] as const

export const PromotionConditionFields = enumOptionsToObject(promotionConditionFieldOptions)

export const PromotionConditionFieldEnum = z.enum(promotionConditionFieldOptions).setEnumLabels({
  outfitterIds: 'Outfitters',
  outfitterGroupIds: 'Outfitter Groups',
  rideBeginDate: 'Ride Begin Date',
  bookingDate: 'Booking Date',
  outfitterProgram: 'Outfitter Program',
  // this is intentionally a bit ugly - special casing number of redemptions here to enhance clarity. The "is Less Than"
  // language was confusing since we think outfitters want to input the exact number of redemptions possible.
  numberOfRedemptions: 'Maximum Number of Redemptions',
  productLines: 'Product Lines',
  productIds: 'Adventure Listings'
})
export type PromotionConditionField = (typeof promotionConditionFieldOptions)[number]

export const promotionConditionOperatorOptions = [
  'isBefore',
  'isAfter',
  'isAnyOf',
  'isLessThan',
  'hasDayOfWeek',
  'is'
] as const

const promotionConditionOperators = z.enum(promotionConditionOperatorOptions)

export const promotionConditionOperatorsLabels = {
  isBefore: 'Is On Or Before',
  isAfter: 'Is On Or After',
  isAnyOf: 'Is Any Of',
  isLessThan: 'Is Less Than',
  hasDayOfWeek: 'Has Day Of Week',
  is: 'Is'
}
export type PromotionConditionOperator = (typeof promotionConditionOperatorOptions)[number]

export function getConditionOperatorsOptionsForField(field: PromotionConditionField): PromotionConditionOperator[] {
  if (field === 'outfitterIds') {
    return ['isAnyOf']
  } else if (field === 'outfitterGroupIds') {
    return ['isAnyOf']
  } else if (field === 'outfitterProgram') {
    return ['is']
  } else if (field === 'bookingDate') {
    return ['isBefore', 'isAfter', 'hasDayOfWeek']
  } else if (field === 'rideBeginDate') {
    return ['isBefore', 'isAfter', 'hasDayOfWeek']
  } else if (field === 'numberOfRedemptions') {
    return ['isLessThan']
  } else if (field === 'productLines') {
    return ['isAnyOf']
  } else if (field === 'productIds') {
    return ['isAnyOf']
  } else {
    return []
  }
}

const promotionCondition = z.object({
  field: PromotionConditionFieldEnum,
  operator: promotionConditionOperators,
  value: z.any()
})
export type promotionCondition = z.infer<typeof promotionCondition>

export const promotionModel = z.object({
  id: z.string(),
  shortId: z.string(),
  versionId: z.number(),
  name: z.string(),
  private: z.boolean(),
  polarisCRMPromo: z.boolean(),
  code: z.string().regex(promocodeRegex).min(3).nullable(),
  outfitterGroupSponsorId: z.number().nullable(),
  unit: z.enum(promotionUnitOptions),
  amount: z.number(),
  conditions: promotionCondition.array(),
  affiliateId: z.string().nullable(),
  createdDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullable()
})

export type promotionModel = z.infer<typeof promotionModel>
